import {
  ChamberMemberProPagePermissionsEnum,
  CmpEventPaymentStatusEnum,
  CmpEventRegistrationStatusEnum,
  CmpEventSponsorshipRequestStatusEnum,
  CmpInvoiceStatusEnum,
} from "../../../../graphql/types";
import {
  isSafeHostedCheckoutUrl,
  SecureCardCheckoutHandoffData,
  SecureCardCheckoutMode,
} from "../../Invoicing/SecureCardCheckoutHandoff";
import type { EventSalesOperationAction } from "./eventSalesOperation";

export type EventSecureCardCheckoutPurpose = "REGISTRATION" | "SPONSORSHIP";

export type EventSecureCardCheckoutConfig = {
  operationAction: EventSalesOperationAction;
  pagePermission: ChamberMemberProPagePermissionsEnum;
  sourceIdInputName: "registrationId" | "sponsorshipRequestId";
};

export const EVENT_SECURE_CARD_CHECKOUT_CONFIG: Record<
  EventSecureCardCheckoutPurpose,
  EventSecureCardCheckoutConfig
> = {
  REGISTRATION: {
    operationAction: "START_REGISTRATION_CARD_CHECKOUT",
    pagePermission:
      ChamberMemberProPagePermissionsEnum.ChamberAdminEventsManageTicketSales,
    sourceIdInputName: "registrationId",
  },
  SPONSORSHIP: {
    operationAction: "START_SPONSORSHIP_CARD_CHECKOUT",
    pagePermission:
      ChamberMemberProPagePermissionsEnum.ChamberAdminEventsManageSponsorshipSales,
    sourceIdInputName: "sponsorshipRequestId",
  },
};

export type EventSecureCardCheckoutReturnLike = {
  amountInCents: number;
  checkoutMode: string;
  checkoutSessionId: string;
  checkoutUrl?: string | null;
  clientSecret?: string | null;
  connectedAccountId?: string | null;
  expiresAtUtcMilli: number;
  generatedAtUtcMilli: number;
  invoiceId: string;
  invoiceNumber: string;
  publishableKey?: string | null;
};

export type EventSecureCardCheckoutSession = Readonly<
  SecureCardCheckoutHandoffData & {
    generatedAtUtcMilli: number;
    invoiceId: string;
    invoiceNumber: string;
  }
>;

export type NormalizeEventSecureCardCheckoutInput = {
  expectedAmountInCents: number;
  expectedInvoiceId: string;
  requestedMode: SecureCardCheckoutMode;
  result: EventSecureCardCheckoutReturnLike;
};

export type EventSecureCardCheckoutStatus = "EXPIRED" | "PAID" | "PENDING";

export type EventSecureCardCheckoutReadback = Pick<
  GetEventSecureCardCheckoutStatusInput,
  "invoiceStatus" | "paymentStatus"
>;

export const getEventSecureCardCheckoutReadback = (
  source: {
    paymentStatus: CmpEventPaymentStatusEnum;
    invoice?: { id: string; invoiceStatus: CmpInvoiceStatusEnum } | null;
  } | null | undefined,
  invoiceId: string,
): EventSecureCardCheckoutReadback => {
  if (!source?.invoice || source.invoice.id !== invoiceId) {
    throw new Error("The current event payment status could not be read. Refresh the invoice and try again.");
  }
  return { invoiceStatus: source.invoice.invoiceStatus, paymentStatus: source.paymentStatus };
};

export type GetEventSecureCardCheckoutStatusInput = {
  expiresAtUtcMilli: number;
  invoiceStatus: CmpInvoiceStatusEnum;
  nowUtcMilli: number;
  paymentStatus: CmpEventPaymentStatusEnum;
};

export type EventSecureCardCheckoutEligibilityInput = {
  amountDueInCents: number;
  invoiceStatus: CmpInvoiceStatusEnum;
  paymentStatus: CmpEventPaymentStatusEnum;
  purpose: EventSecureCardCheckoutPurpose;
  sourceStatus:
    CmpEventRegistrationStatusEnum | CmpEventSponsorshipRequestStatusEnum;
};

export type EventSecureCardCheckoutMutationInput = {
  amountInCents: number;
  chamberId: string;
  eventId: string;
  fallbackForCheckoutSessionId?: string;
  invoiceId: string;
  mode: SecureCardCheckoutMode;
  operationId: string;
  registrationId?: string;
  sponsorshipRequestId?: string;
};

export type ExecuteEventSecureCardCheckoutMutationInput<Result> = {
  executeRegistration: (
    input: EventSecureCardCheckoutMutationInput,
  ) => Promise<Result>;
  executeSponsorship: (
    input: EventSecureCardCheckoutMutationInput,
  ) => Promise<Result>;
  input: EventSecureCardCheckoutMutationInput;
  purpose: EventSecureCardCheckoutPurpose;
};

const isValidMode = (value: string): value is SecureCardCheckoutMode =>
  value === "CUSTOM" || value === "HOSTED_FALLBACK";

export const normalizeEventSecureCardCheckout = ({
  expectedAmountInCents,
  expectedInvoiceId,
  requestedMode,
  result,
}: NormalizeEventSecureCardCheckoutInput): EventSecureCardCheckoutSession => {
  if (
    result.invoiceId !== expectedInvoiceId ||
    !result.invoiceNumber.trim() ||
    !result.checkoutSessionId.trim()
  ) {
    throw new Error(
      "The secure checkout response did not match the selected event invoice.",
    );
  }

  if (
    !isValidMode(result.checkoutMode) ||
    result.checkoutMode !== requestedMode
  ) {
    throw new Error(
      "The secure checkout mode did not match the requested payment form.",
    );
  }

  if (
    !Number.isSafeInteger(result.amountInCents) ||
    result.amountInCents <= 0 ||
    result.amountInCents !== expectedAmountInCents
  ) {
    throw new Error(
      "The secure checkout amount did not match the selected amount.",
    );
  }

  if (
    !Number.isFinite(result.generatedAtUtcMilli) ||
    !Number.isFinite(result.expiresAtUtcMilli) ||
    result.expiresAtUtcMilli <= result.generatedAtUtcMilli
  ) {
    throw new Error("The secure checkout expiry was invalid.");
  }

  const publishableKey = result.publishableKey?.trim();
  const connectedAccountId = result.connectedAccountId?.trim();

  if (!publishableKey || !connectedAccountId) {
    throw new Error("The Stripe payment configuration was incomplete.");
  }

  if (requestedMode === "CUSTOM") {
    if (!result.clientSecret?.trim() || result.checkoutUrl) {
      throw new Error("The custom secure card form was incomplete.");
    }
  } else if (
    result.clientSecret ||
    !isSafeHostedCheckoutUrl(result.checkoutUrl)
  ) {
    throw new Error("The hosted fallback was not a valid Stripe Checkout URL.");
  }

  return Object.freeze({
    amountInCents: result.amountInCents,
    checkoutMode: result.checkoutMode,
    checkoutSessionId: result.checkoutSessionId,
    checkoutUrl: result.checkoutUrl,
    clientSecret: result.clientSecret,
    connectedAccountId,
    expiresAtUtcMilli: result.expiresAtUtcMilli,
    generatedAtUtcMilli: result.generatedAtUtcMilli,
    invoiceId: result.invoiceId,
    invoiceNumber: result.invoiceNumber,
    publishableKey,
  });
};

/** Paid is shown only after both authoritative event and invoice states agree. */
export const getEventSecureCardCheckoutStatus = ({
  expiresAtUtcMilli,
  invoiceStatus,
  nowUtcMilli,
  paymentStatus,
}: GetEventSecureCardCheckoutStatusInput): EventSecureCardCheckoutStatus => {
  if (
    paymentStatus === CmpEventPaymentStatusEnum.Paid &&
    invoiceStatus === CmpInvoiceStatusEnum.Paid
  ) {
    return "PAID";
  }

  return nowUtcMilli >= expiresAtUtcMilli ? "EXPIRED" : "PENDING";
};

const eligibleRegistrationStatuses = new Set<CmpEventRegistrationStatusEnum>([
  CmpEventRegistrationStatusEnum.PaymentPending,
]);

const eligibleSponsorshipStatuses =
  new Set<CmpEventSponsorshipRequestStatusEnum>([
    CmpEventSponsorshipRequestStatusEnum.ApprovedAwaitingPayment,
    CmpEventSponsorshipRequestStatusEnum.PaymentActionRequired,
  ]);

/**
 * This check controls access to the card form, including resuming a pending
 * checkout. The backend validates and reopens the exact existing Session.
 */
export const canStartEventSecureCardCheckout = ({
  amountDueInCents,
  invoiceStatus,
  paymentStatus,
  purpose,
  sourceStatus,
}: EventSecureCardCheckoutEligibilityInput): boolean => {
  if (
    !Number.isSafeInteger(amountDueInCents) ||
    amountDueInCents <= 0 ||
    invoiceStatus !== CmpInvoiceStatusEnum.Unpaid ||
    ![CmpEventPaymentStatusEnum.Unpaid, CmpEventPaymentStatusEnum.PaymentPending].includes(paymentStatus)
  ) {
    return false;
  }

  // Registrations saved before August 2026 stay Confirmed with an unpaid
  // invoice. The backend adopts those legacy sales into Payment Pending when
  // the card checkout starts, so the dashboard must not hide the card option.
  return purpose === "REGISTRATION"
    ? eligibleRegistrationStatuses.has(
        sourceStatus as CmpEventRegistrationStatusEnum,
      ) ||
        (paymentStatus === CmpEventPaymentStatusEnum.Unpaid &&
          sourceStatus === CmpEventRegistrationStatusEnum.Confirmed)
    : (paymentStatus === CmpEventPaymentStatusEnum.PaymentPending &&
        sourceStatus === CmpEventSponsorshipRequestStatusEnum.PaymentPending) ||
      eligibleSponsorshipStatuses.has(
        sourceStatus as CmpEventSponsorshipRequestStatusEnum,
      );
};

export const LEGACY_LIVE_SPONSORSHIP_CARD_UNAVAILABLE_MESSAGE =
  "Card payment is not available for this sponsorship, because it was approved before it was paid. Select another payment method to record the payment.";

// A sponsorship made live before payment cannot start card checkout, so a
// refresh does not help. Give staff the next step instead.
export const getEventSecureCardUnavailableMessage = ({
  fallback,
  paymentStatus,
  purpose,
  sourceStatus,
}: {
  fallback: string;
  paymentStatus?: string | null;
  purpose: EventSecureCardCheckoutEligibilityInput["purpose"];
  sourceStatus?: string | null;
}): string =>
  purpose === "SPONSORSHIP" &&
  sourceStatus === CmpEventSponsorshipRequestStatusEnum.Live &&
  paymentStatus === CmpEventPaymentStatusEnum.Unpaid
    ? LEGACY_LIVE_SPONSORSHIP_CARD_UNAVAILABLE_MESSAGE
    : fallback;

export const buildEventSecureCardCheckoutInput = ({
  amountInCents,
  chamberId,
  eventId,
  fallbackForCheckoutSessionId,
  invoiceId,
  mode,
  operationId,
  purpose,
  sourceId,
}: {
  amountInCents: number;
  chamberId: string;
  eventId: string;
  fallbackForCheckoutSessionId?: string;
  invoiceId: string;
  mode: SecureCardCheckoutMode;
  operationId: string;
  purpose: EventSecureCardCheckoutPurpose;
  sourceId: string;
}): EventSecureCardCheckoutMutationInput => ({
  amountInCents,
  chamberId,
  eventId,
  invoiceId,
  mode,
  operationId,
  ...(fallbackForCheckoutSessionId ? { fallbackForCheckoutSessionId } : {}),
  ...(purpose === "REGISTRATION"
    ? { registrationId: sourceId }
    : { sponsorshipRequestId: sourceId }),
});

/** Keeps the two purpose-specific network actions explicit. */
export const executeEventSecureCardCheckoutMutation = async <Result>({
  executeRegistration,
  executeSponsorship,
  input,
  purpose,
}: ExecuteEventSecureCardCheckoutMutationInput<Result>): Promise<Result> =>
  purpose === "REGISTRATION"
    ? executeRegistration(input)
    : executeSponsorship(input);
